"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { UTM_SESSION_COOKIE, UTM_FIRST_TOUCH_COOKIE, UTM_SESSION_TTL_SECONDS, UTM_PARAM_KEYS, CLICK_ID_PARAM_KEYS, CLICK_ID_COOKIE_NAMES, decodeUtmCookieValue, extractClickIdParams, extractUtmParams, mergeAttributionQueryParams, parseFbcCookieValue, parseGclCookieValue, toFirstTouchProperties, type AttributionQueryParams, type UtmParams } from "./utm-attribution";
import { getMixpanelClientSync } from "../../mixpanel/lib/mixpanel-client";
function readCookie(name: string): string | undefined {
  const match = document.cookie.split("; ").find(row => row.startsWith(`${name}=`));
  if (!match) {
    return undefined;
  }
  return decodeURIComponent(match.slice(name.length + 1));
}

// Cookies scoped to the parent domain (set by the proxy) only apply that
// domain outside production, where the app runs on a different registrable
// domain (e.g. a Vercel preview URL) and the browser would reject it.
function parentDomainAttribute(): string {
  return window.location.hostname.endsWith("artofproblemsolving.com") ? "; domain=.artofproblemsolving.com" : "";
}
export function getSessionUtmParams(): UtmParams | null {
  return decodeUtmCookieValue(readCookie(UTM_SESSION_COOKIE));
}
export function getFirstTouchUtmParams(): UtmParams | null {
  return decodeUtmCookieValue(readCookie(UTM_FIRST_TOUCH_COOKIE));
}
function clickIdFromCookie(key: (typeof CLICK_ID_PARAM_KEYS)[number]) {
  const raw = readCookie(CLICK_ID_COOKIE_NAMES[key]);
  if (key === "fbclid") {
    return parseFbcCookieValue(raw);
  }
  return parseGclCookieValue(raw);
}
function collectAttributionParams(): AttributionQueryParams {
  const pageSearch = new URLSearchParams(window.location.search);
  const params: AttributionQueryParams = {};
  const sessionUtm = getSessionUtmParams();
  const pageUtm = extractUtmParams(pageSearch);
  for (const key of UTM_PARAM_KEYS) {
    const value = sessionUtm?.[key] ?? pageUtm?.[key];
    if (value) {
      params[key] = value;
    }
  }
  const pageClickIds = extractClickIdParams(pageSearch);
  for (const key of CLICK_ID_PARAM_KEYS) {
    const value = pageClickIds[key] ?? clickIdFromCookie(key);
    if (value) {
      params[key] = value;
    }
  }
  return params;
}

/**
 * Copy session UTMs and ad click IDs onto an outbound URL so attribution
 * survives hops to domains our cookies cannot follow. Existing query keys
 * and the hash are left alone. Safe to call during SSR (returns `url`).
 */
export function appendAttributionQueryParams(url: string): string {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return url;
  }
  return mergeAttributionQueryParams(url, collectAttributionParams());
}

/**
 * Mutate an anchor's href at click time so a click before the mount
 * decoration effect still carries attribution.
 */
export function decorateAttributionAnchorClick(event: {
  currentTarget: {
    href: string;
  };
}, originalHref: string): void {
  event.currentTarget.href = appendAttributionQueryParams(originalHref);
}

/**
 * After mount, return `href` decorated with session UTMs and click IDs.
 * The first render (SSR / hydration) keeps the original href so markup
 * matches; a follow-up render updates it for middle-click and copy-link.
 */
export function useAttributionHref(href) {
  const $ = _c(3);
  const [sourceHref, setSourceHref] = useState(href);
  const [decorated, setDecorated] = useState(href);
  if (href !== sourceHref) {
    setSourceHref(href);
    setDecorated(href);
  }
  let t0;
  let t1;
  if ($[0] !== href) {
    t0 = () => {
      if (!href) {
        setDecorated(href);
        return;
      }
      setDecorated(appendAttributionQueryParams(href));
    };
    t1 = [href];
    $[0] = href;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return decorated;
}

/**
 * Slide the session-attribution cookie's expiry forward without changing its
 * value. Call on every tracked client-side event so campaign attribution
 * survives ongoing activity within a session, not just page navigations
 * (which the proxy already refreshes on its own).
 *
 * Only for the session cookie: the first-touch cookie is written once by the
 * proxy and must never be touched from client JS, since Safari ITP caps
 * script-set cookies at 7 days regardless of the Max-Age given.
 */
export function touchSessionUtmCookie(): void {
  const value = readCookie(UTM_SESSION_COOKIE);
  if (!value) {
    return;
  }
  document.cookie = `${UTM_SESSION_COOKIE}=${encodeURIComponent(value)}; max-age=${UTM_SESSION_TTL_SECONDS}; path=/; secure; samesite=lax${parentDomainAttribute()}`;
}

/**
 * Registers session + first-touch UTM attribution as Mixpanel super
 * properties, so every subsequent .track() call (autocapture included)
 * carries them automatically via Mixpanel's own super-property propagation,
 * instead of every call site threading them in manually. Call once per page
 * load, after Mixpanel is initialized — not per event. We own the TTL via
 * our own cookie (30-min rolling), independent of Mixpanel's 365-day
 * default `cookie_expiration`.
 */
export function registerUtmSuperProperties(): void {
  const mixpanel = getMixpanelClientSync();
  if (!mixpanel) {
    return;
  }
  mixpanel.register({
    ...getSessionUtmParams(),
    ...toFirstTouchProperties(getFirstTouchUtmParams())
  });
}